import React, { useState, useRef, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import Header from './components/Header';
import Hero from './components/Hero';
import News from './components/News';
import PatchNotesDetail from './components/PatchNotesDetail';
import 'react-photo-view/dist/react-photo-view.css';
import Footer from './components/Footer';
import CharactersPage from './pages/CharactersPage';
import ServersPage from './pages/ServersPage';
import SpaPage from './pages/SpaPage';
import SteamCallback from './pages/SteamCallback';
import ErrorPage from './pages/ErrorPage';
import SocialBar from './components/SocialBar';
import MapPage from './pages/MapPage';
import PostsPage from './pages/PostsPage';
import NewsDetail from './components/NewsDetail';
import UserProfilePage from './pages/UserProfilePage';
import CreatePostPage from "./pages/CreatePostPage";
import PostDetail from "./components/PostDetail";
import LoadingBar from 'react-top-loading-bar';
import './css/app.css';
import './css/loadingBar.css';
import SettingsPage from "./pages/SettingsPage";
import MyWaitingPosts from "./components/MyWaitingPosts";
import SurveyPage from "./pages/SurveyPage";
import SurveyPageInactive from "./pages/SurveyPageInactive";
import SearchPage from "./pages/SearchPage";
import ScrollToTop from './components/ScrollToTop';

function MainApp({ scrollbarRef, isNavbarOpen, setIsNavbarOpen, closeNavbar }) {
    const [isMuted, setIsMuted] = useState(true);
    const [setIsNewsLoaded] = useState(false);
    const [loadingProgress, setLoadingProgress] = useState(0);
    const homeRef = useRef(null);
    const newsRef = useRef(null);
    const galleryRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();

    const toggleMute = () => {
        setIsMuted(prev => !prev);
    };

    const scrollToSection = (sectionRef) => {
        if (scrollbarRef.current && sectionRef.current) {
            const position = sectionRef.current.offsetTop;
            scrollbarRef.current.view.scrollTo({
                top: position,
                behavior: 'smooth'
            });
        }
    };

    const onHomeClick = () => {
        if (location.pathname !== '/') {
            navigate('/');
        } else {
            scrollToSection(homeRef);
        }
    };

    const onNewsClick = () => {
        if (location.pathname !== '/') {
            navigate('/');
            setTimeout(() => scrollToSection(newsRef), 200);
        } else {
            scrollToSection(newsRef);
        }
    };

    useEffect(() => {
        setLoadingProgress(30);

        const loadContent = new Promise((resolve) => {
            setTimeout(() => {
                resolve();
            }, 500);
        });

        loadContent.then(() => {
            setLoadingProgress(100);
        });
    }, [location]);

    useEffect(() => {
        if (location.state?.scrollTo) {
            const element = document.querySelector(location.state.scrollTo);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
            }
        }
    }, [location]);

    return (
        <div className="app-container" ref={homeRef}>
            <LoadingBar
                color="rgba(255, 0, 85, 1)"
                className="custom-loading-bar"
                progress={loadingProgress}
                onLoaderFinished={() => setLoadingProgress(0)}
            />
            <Header
                onHomeClick={onHomeClick}
                onNewsClick={onNewsClick}
                toggleMute={toggleMute}
                isMuted={isMuted}
                isNavbarOpen={isNavbarOpen}
                setIsNavbarOpen={setIsNavbarOpen}
                closeNavbar={closeNavbar}
            />
            <SocialBar />
            <main className="main-content">
                <ScrollToTop scrollbarRef={scrollbarRef} />
                <Routes>
                    <Route
                        path="/"
                        element={
                            <>
                                <Hero isMuted={isMuted} onNewsClick={onNewsClick} />
                                <div ref={newsRef}>
                                    <News onLoad={() => setIsNewsLoaded(true)} />
                                </div>
                                <div ref={galleryRef}></div>
                            </>
                        }
                    />
                    <Route path="/user/:userId" element={<UserProfilePage />} />
                    <Route path="/characters" element={<CharactersPage />} />
                    <Route path="/spa" element={<SpaPage />} />
                    <Route path="/map" element={<MapPage />} />
                    <Route path="/servers" element={<ServersPage />} />
                    <Route path="/auth/steam/callback" element={<SteamCallback />} />
                    <Route path="/error" element={<ErrorPage />} />
                    <Route path="/news/:id" element={<NewsDetail />} />
                    <Route path="/patch-notes/:id" element={<PatchNotesDetail />} />
                    <Route path="/posts" element={<PostsPage />} />
                    <Route path="/user/:id" element={<UserProfilePage />} />
                    <Route path="/create-post" element={<CreatePostPage />} />
                    <Route path="/posts/:id" element={<PostDetail />} />
                    <Route path="/settings" element={<SettingsPage />} />
                    <Route path="/survey" element={<SurveyPage />} />
                    <Route path="/surveys/inactive" element={<SurveyPageInactive />} />
                    <Route path="/my-waiting-posts" element={<MyWaitingPosts />} />
                    <Route path="/search" element={<SearchPage />} />
                    <Route path="*" element={<ErrorPage />} />
                </Routes>
            </main>
            <Footer />
        </div>
    );
}

export default MainApp;
